"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Navigation, CircleCheckBig } from "lucide-react";
import type { MutableRefObject } from "react";
import type { TradingChartRef } from "@/components/TradingChart/TradingChart";
import { apiClient } from "@/lib/api-client";
import { getViewModeChrome } from "../view-mode-chrome";

// ISO timestamp to Unix timestamp converter
const convertToUnix = (dateString: string): number => {
  
  // Parse ISO 8601 timestamp directly
  const date = new Date(dateString);
  
  const result = Math.floor(date.getTime() / 1000);
  
  return result;
};

interface SetViewportSuccessProps {
  args?: any;
  result?: any;
  chartRef?: MutableRefObject<TradingChartRef | null>;
  viewMode?: string;
}

export function SetViewportSuccess({
  args,
  chartRef,
  viewMode,
}: SetViewportSuccessProps) {
  const chrome = getViewModeChrome(viewMode);
  const hasSetViewportRef = useRef(false);

  useEffect(() => {
    // Prevent double execution in React Strict Mode
    if (hasSetViewportRef.current) return;

    if (!chartRef?.current || !args?.time1 || !args?.time2) return;

    hasSetViewportRef.current = true;

    const captureAndSendContext = async () => {
      try {
       
        // Convert ISO timestamps to Unix timestamps
        const unixTime1 = convertToUnix(args.time1);
        const unixTime2 = convertToUnix(args.time2);
        
        // Call setViewport with converted times and optional prices
        chartRef.current?.setViewport(
          unixTime1,
          unixTime2,
          args.price1 || null,
          args.price2 || null
        );

        const screenshot = await chartRef.current?.captureBase64?.("image/png");
        const viewport = chartRef.current?.getViewport?.();
      } catch (error) {
        // Silent error handling
      }
    };

    captureAndSendContext();
  }, []);

  const title = "Viewport updated";
  const subtitle =
    args?.price1 && args?.price2
      ? "Time range and price levels set"
      : "Time range adjusted";

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="w-full"
    >
      <div className={`rounded-md border ${chrome.card} overflow-hidden`}>
        <div className="px-3 py-2 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-2.5">
            <Navigation
                className={`w-3.5 h-3.5 ${chrome.iconMuted} shrink-0`}
                strokeWidth={2}
              />
            <div className="flex flex-col">
              <span className={`text-[13px] font-semibold ${chrome.text} truncate`}>
                {title}
              </span>
              <span className={`text-[11px] ${chrome.subtext} truncate`}>
                {subtitle}
              </span>
            </div>
          </div>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
          >
            <CircleCheckBig className="h-4 w-4 text-green-500" />
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
